import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { usePromptSlot } from '../utils/onboardingQueue';

const PROMPT_KEY = 'ht_lang_prompted';
const LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

/* First sign-in language prompt: shown once per device when the user has never
   chosen a language. Queued via onboardingQueue so it never stacks on other prompts. */
export default function LanguagePromptDialog() {
  const { i18n } = useTranslation();
  const [wants, setWants] = useState(false);
  const open = usePromptSlot('language', wants);

  useEffect(() => {
    const chosen = localStorage.getItem('hinestime_language');
    const prompted = localStorage.getItem(PROMPT_KEY);
    if (!chosen && !prompted) {
      const t = setTimeout(() => setWants(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  if (!open) return null;

  const browserLang = (navigator.language || 'en').split('-')[0];

  const pick = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('hinestime_language', code);
    localStorage.setItem(PROMPT_KEY, '1');
    setWants(false);
  };

  return (
    <div
      className="fixed inset-0 z-[125] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) pick(browserLang === 'en' ? 'en' : browserLang); }}
      data-testid="language-prompt-dialog"
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Choose your language</h2>
            <p className="text-xs text-slate-500">Elige tu idioma · Choisissez votre langue · Sprache wählen</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border text-left text-sm font-medium transition-colors hover:bg-blue-50 dark:hover:bg-slate-800 ${
                browserLang === l.code
                  ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                  : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'
              }`}
              data-testid={`language-prompt-${l.code}`}
            >
              <span className="text-xl">{l.flag}</span>
              {l.label}
              {browserLang === l.code && <span className="ml-auto text-[10px] uppercase tracking-wide text-blue-500">Suggested</span>}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3 text-center">You can change this anytime in Settings → Language</p>
      </div>
    </div>
  );
}
